import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Paperclip } from 'lucide-react';
import RichEmailToolbar from './RichEmailToolbar';
import SourceCodeModal from './SourceCodeModal';
import LinkInsertPopover from './LinkInsertPopover';
import ImageInsertPopover from './ImageInsertPopover';
import CustomValuesPicker from './CustomValuesPicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE,
  DEFAULT_LINE_HEIGHT,
  DEFAULT_PARAGRAPH_STYLE,
  DEFAULT_TEXT_COLOR,
  applyBackgroundColor,
  applyBlockFormat,
  applyFontFamilyCss,
  applyLineHeight,
  applyStyleToSelection,
  applyTextColor,
  ensureEditorHtml,
  execEditorCommand,
  getSelectedText,
  insertHtmlAtCursor,
  saveSelection,
} from '../../utils/richEmailEditorUtils';
import { insertIntoContentEditable } from '../../utils/insertMergeField';

const RichEmailEditor = forwardRef(({
  value = '',
  onChange,
  disabled = false,
  onWriteWithAIClick,
  onAttachmentsClick,
  attachments = [],
  placeholder = 'Type your email content here...',
  showAttachments = true,
  footer,
  token,
}, ref) => {
  const editorRef = useRef(null);
  const mergeFieldsButtonRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const activeTextColorSpanRef = useRef(null);
  const activeBackgroundSpanRef = useRef(null);
  const isInternalUpdate = useRef(false);

  const [fontFamily, setFontFamily] = useState(DEFAULT_FONT_FAMILY);
  const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
  const [blockType, setBlockType] = useState('p');
  const [lineHeight, setLineHeight] = useState(DEFAULT_LINE_HEIGHT);
  const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR);
  const [backgroundColor, setBackgroundColor] = useState('#ffff00');
  const [canRedo, setCanRedo] = useState(false);

  const [showSourceCode, setShowSourceCode] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [showMergePicker, setShowMergePicker] = useState(false);
  const [selectedText, setSelectedText] = useState('');

  const emitChange = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    isInternalUpdate.current = true;
    onChange?.(html);
  }, [onChange]);

  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    getHtml: () => editorRef.current?.innerHTML || '',
    setHtml: (html) => {
      if (editorRef.current) {
        editorRef.current.innerHTML = ensureEditorHtml(html);
        isInternalUpdate.current = true;
      }
    },
    insertHtml: (html) => {
      insertHtmlAtCursor(editorRef.current, html);
      emitChange();
    },
  }), [emitChange]);

  useEffect(() => {
    if (!editorRef.current || isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const next = ensureEditorHtml(value);
    if (editorRef.current.innerHTML !== next) {
      editorRef.current.innerHTML = next;
    }
  }, [value]);

  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.innerHTML = ensureEditorHtml(value);
  }, []);

  const runCommand = (fn) => {
    if (disabled) return;
    fn();
    emitChange();
    try {
      setCanRedo(document.queryCommandEnabled('redo'));
    } catch {
      setCanRedo(false);
    }
  };

  const handleFontFamily = (family) => {
    setFontFamily(family);
    runCommand(() => applyStyleToSelection(editorRef.current, {
      fontFamily: applyFontFamilyCss(family),
    }));
  };

  const handleFontSize = (size) => {
    setFontSize(size);
    runCommand(() => applyStyleToSelection(editorRef.current, { fontSize: size }));
  };

  const handleBlockType = (tag) => {
    setBlockType(tag);
    runCommand(() => applyBlockFormat(editorRef.current, tag));
  };

  const handleLineHeight = (lh) => {
    setLineHeight(lh);
    runCommand(() => applyLineHeight(editorRef.current, lh));
  };

  const saveEditorSelection = useCallback(() => {
    activeTextColorSpanRef.current = null;
    activeBackgroundSpanRef.current = null;
    savedSelectionRef.current = saveSelection(editorRef.current);
  }, []);

  const handleTextColor = (hex) => {
    setTextColor(hex);
    runCommand(() => {
      if (activeTextColorSpanRef.current?.isConnected) {
        activeTextColorSpanRef.current.style.color = hex;
        return;
      }
      const span = applyTextColor(editorRef.current, hex, savedSelectionRef.current);
      if (span) activeTextColorSpanRef.current = span;
    });
  };

  const handleBackgroundColor = (hex) => {
    setBackgroundColor(hex);
    runCommand(() => {
      if (activeBackgroundSpanRef.current?.isConnected) {
        activeBackgroundSpanRef.current.style.backgroundColor = hex;
        return;
      }
      const span = applyBackgroundColor(editorRef.current, hex, savedSelectionRef.current);
      if (span) activeBackgroundSpanRef.current = span;
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertParagraph', false);
      const selection = window.getSelection();
      if (selection?.anchorNode) {
        let node = selection.anchorNode;
        if (node.nodeType === Node.TEXT_NODE) node = node.parentElement;
        if (node?.tagName === 'P' && !node.getAttribute('style')) {
          node.setAttribute('style', DEFAULT_PARAGRAPH_STYLE);
        }
      }
      emitChange();
    }
  };

  const openLinkModal = () => {
    setSelectedText(getSelectedText(editorRef.current));
    setShowLinkModal(true);
  };

  const handleLinkInsert = (html) => {
    runCommand(() => insertHtmlAtCursor(editorRef.current, html));
  };

  const handleImageInsert = (html) => {
    runCommand(() => insertHtmlAtCursor(editorRef.current, html));
  };

  const handleSourceSave = (html) => {
    if (editorRef.current) {
      editorRef.current.innerHTML = html;
      emitChange();
    }
  };

  return (
    <div className="relative" data-testid="rich-email-editor">
      {showAttachments && (
        <div className="flex items-center justify-end mb-1">
          <button
            type="button"
            onClick={() => {
              onAttachmentsClick?.();
              setShowAttachmentsModal(true);
            }}
            className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
            data-testid="add-attachments-button"
          >
            <Paperclip size={12} />
            Add attachments
            {attachments.length > 0 && (
              <span className="text-slate-500">({attachments.length})</span>
            )}
          </button>
        </div>
      )}

      <div className="relative">
        <RichEmailToolbar
          disabled={disabled}
          fontFamily={fontFamily}
          fontSize={fontSize}
          blockType={blockType}
          lineHeight={lineHeight}
          textColor={textColor}
          backgroundColor={backgroundColor}
          canRedo={canRedo}
          onBeforeColorPickerOpen={saveEditorSelection}
          onFontFamilyChange={handleFontFamily}
          onFontSizeChange={handleFontSize}
          onBlockTypeChange={handleBlockType}
          onLineHeightChange={handleLineHeight}
          onTextColorChange={handleTextColor}
          onBackgroundColorChange={handleBackgroundColor}
          onMergeFields={() => setShowMergePicker(true)}
          mergeFieldsButtonRef={mergeFieldsButtonRef}
          onWriteWithAI={onWriteWithAIClick}
          onUndo={() => runCommand(() => execEditorCommand('undo'))}
          onRedo={() => runCommand(() => execEditorCommand('redo'))}
          onBold={() => runCommand(() => execEditorCommand('bold'))}
          onItalic={() => runCommand(() => execEditorCommand('italic'))}
          onUnderline={() => runCommand(() => execEditorCommand('underline'))}
          onStrikethrough={() => runCommand(() => execEditorCommand('strikeThrough'))}
          onBulletList={() => runCommand(() => execEditorCommand('insertUnorderedList'))}
          onNumberedList={() => runCommand(() => execEditorCommand('insertOrderedList'))}
          onAlignLeft={() => runCommand(() => execEditorCommand('justifyLeft'))}
          onAlignCenter={() => runCommand(() => execEditorCommand('justifyCenter'))}
          onAlignRight={() => runCommand(() => execEditorCommand('justifyRight'))}
          onIndent={() => runCommand(() => execEditorCommand('indent'))}
          onOutdent={() => runCommand(() => execEditorCommand('outdent'))}
          onLink={openLinkModal}
          onImage={() => setShowImageModal(true)}
          onSourceCode={() => setShowSourceCode(true)}
        />
        <CustomValuesPicker
          open={showMergePicker}
          onClose={() => setShowMergePicker(false)}
          anchorRef={mergeFieldsButtonRef}
          token={token}
          onInsert={(mergeToken) => {
            runCommand(() => insertIntoContentEditable(editorRef.current, mergeToken));
            setShowMergePicker(false);
          }}
        />
      </div>

      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emitChange}
        onKeyDown={handleKeyDown}
        data-testid="rich-email-editor-body"
        data-placeholder={placeholder}
        className="min-h-[200px] p-4 border border-t-0 border-slate-200 rounded-b-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 prose prose-sm max-w-none rich-email-editor-body empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400"
        style={{ minHeight: '200px', lineHeight: DEFAULT_LINE_HEIGHT }}
      />

      {footer}

      <SourceCodeModal
        isOpen={showSourceCode}
        onClose={() => setShowSourceCode(false)}
        html={editorRef.current?.innerHTML || value}
        onSave={handleSourceSave}
        token={token}
      />

      <LinkInsertPopover
        isOpen={showLinkModal}
        onClose={() => setShowLinkModal(false)}
        selectedText={selectedText}
        onInsert={handleLinkInsert}
        token={token}
      />

      <ImageInsertPopover
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onInsert={handleImageInsert}
      />

      <Dialog open={showAttachmentsModal} onOpenChange={setShowAttachmentsModal}>
        <DialogContent className="max-w-md" data-testid="attachments-modal">
          <DialogHeader>
            <DialogTitle>Attachments</DialogTitle>
            <DialogDescription className="sr-only">
              Manage email attachments for this workflow action.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Attachment upload will connect to the workflow email attachment service.
            Files are stored on the email action config as <code>attachments: []</code>.
          </p>
          <Button variant="outline" onClick={() => setShowAttachmentsModal(false)}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
});

RichEmailEditor.displayName = 'RichEmailEditor';

export default RichEmailEditor;
